import {
  rememberSignupProfile,
  startSignup,
} from "@app/helpers/auth/signup.helper";
import { isWebSignupAllowed } from "@app/helpers/auth/signup-availability.helper";
import { resolveAuthSession } from "./helpers/auth/auth-session.helper";
import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { DndProvider } from "react-dnd";
import { Outlet, useNavigate } from "react-router-dom";
import { TouchBackend } from "react-dnd-touch-backend";
import { HTML5Backend } from "react-dnd-html5-backend";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { isLogoutRedirectPending, useAuth } from "@app/hooks";
import { StorageKey } from "@app/constants";
import { isMobileDevice } from "@app/helpers";
import { AppProvider } from "./AppContext";

function App({ instance }: { instance: PublicClientApplication }) {
  const [cookies] = useCookies([StorageKey.AUTH_ACCESS_TOKEN]);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [activeInstance, setActiveInstance] = useState(instance);
  const [authReady, setAuthReady] = useState(false);
  const sessionPromise = useRef<ReturnType<typeof resolveAuthSession> | null>(
    null,
  );

  useEffect(() => {
    if (isLogoutRedirectPending()) return;

    let cancelled = false;
    const signupPending = sessionStorage.getItem("signupInProgress") === "true";
    if (cookies?.tokens?.accessToken && !signupPending) {
      setAuthReady(true);
      return;
    }

    // Share one callback operation across rerenders and Strict Mode effects.
    // MsalProvider mounts only after this operation has saved the session.
    if (!sessionPromise.current)
      sessionPromise.current = resolveAuthSession(instance);
    sessionPromise.current
      .then(({ instance: resolvedInstance, response, isSignup }) => {
        if (cancelled) return;
        if (!response) {
          sessionStorage.removeItem("signupInProgress");
          navigate("/login", { replace: true });
          return;
        }
        if (isSignup) rememberSignupProfile();
        login(response);
        sessionStorage.removeItem("signupInProgress");
        setActiveInstance(resolvedInstance);
        if (isSignup) {
          navigate("/signup/profile", { replace: true });
          return;
        }
        setAuthReady(true);
      })
      .catch(async (error) => {
        if (cancelled) return;
        console.error("Error in App auth flow:", error);
        sessionStorage.removeItem("signupInProgress");
        if (!signupPending && error.errorMessage?.includes("AADB2C99002")) {
          try {
            if (await isWebSignupAllowed()) {
              await startSignup();
              return;
            }
          } catch {
            /* Show login to allow retry. */
          }
        }
        navigate("/login", { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [cookies, instance, login, navigate]);

  if (!authReady) return <></>;

  return (
    <MsalProvider instance={activeInstance}>
      <AppProvider>
        <DndProvider backend={isMobileDevice ? TouchBackend : HTML5Backend}>
          <Outlet />
        </DndProvider>
      </AppProvider>
    </MsalProvider>
  );
}

export default App;
